import * as React from 'react';
import { useParams } from 'react-router-dom';

import { usePreview } from './api';
import { CmsPageSkeleton, useDocumentMetadata } from './cms-page';
import { NotFoundPage } from './not-found-page';
import { Sections } from './sections-panel';
import { pageSections, text, type PreviewContent } from './types';

/**
 * A draft, rendered as the real site will render it, to whoever holds the link (`CMS-012`).
 *
 * ## What this shares with `CmsPage`, and what it deliberately does not
 *
 * It renders through the same `<Sections>` as a published page, and that is the whole point of
 * the story: *"exactly how it will look"* is worth nothing if the preview draws the page a
 * different way. What it does **not** share is `CmsPage` itself, because `CmsPage` takes a
 * `PublishedContent` and this has a `PreviewContent` — two types that are not assignable to one
 * another, on purpose. One component accepting either is one refactor away from a draft on the
 * live site.
 *
 * ## The banner is AC-5's second half and is not decorative
 *
 * *"the previewed page is visibly marked as a preview"*. The failure it prevents is specific and
 * has a shape: somebody screenshots a preview, or forwards the URL, or comes back to the tab an
 * hour later, and takes what they are reading for the live site — then asks why the published
 * page says something else, or worse, quotes it. So the mark is:
 *
 *   - **Above the content, not below it.** A reader who scrolls halfway and stops must already
 *     have passed it.
 *   - **In the document, always**, rather than a dismissible toast. There is nothing to dismiss;
 *     the page does not stop being a draft.
 *   - **In the tab title too.** A forwarded screenshot loses the banner, and a tab among fifteen
 *     is read by its title. Both are how a preview gets mistaken for the site.
 *   - **Carrying the expiry**, because the reader's next question when it stops working is
 *     whether it broke or ran out, and only one of those is worth reporting.
 *
 * `noIndex` is set for the same reason at machine scale: a preview URL that reached a crawler
 * would put an unpublished page in a search index, which outlives the token by months.
 */
export function CmsPreviewPage(): React.ReactElement {
  const { token } = useParams<{ token: string }>();
  const preview = usePreview(token);

  if (preview.isLoading) return <CmsPageSkeleton />;
  // Any failure is the same failure: absent, malformed, expired, wrong, or an entry that has
  // since been deleted. The API answers 404 to all of them (`CMS-012` AC-4) and this does not
  // try to tell them apart — a page that distinguished "expired" from "never existed" would be
  // the oracle the API refuses to be, rebuilt in the client.
  if (preview.data === undefined) return <NotFoundPage />;

  return <PreviewedDraft content={preview.data} />;
}

/**
 * The draft and its mark, with no query behind it.
 *
 * Exported and separate from {@link CmsPreviewPage} for the reason `form-section-panel.tsx` gives
 * about its own split: the container's job is the fetch, and `usePreview` deliberately sets
 * `gcTime: 0` so a draft is not left sitting in the query cache after the page unmounts. That is
 * right for the application and makes the container untestable by seeding a cache — the entry is
 * collected before it can be read. So AC-5's assertions mount this, which is where the criterion
 * actually lives.
 */
export function PreviewedDraft({
  content,
}: {
  readonly content: PreviewContent;
}): React.ReactElement {
  const seo = (content.data['seo'] ?? {}) as Record<string, unknown>;
  const pageTitle = text(seo as never, 'metaTitle') || text(content.data, 'title') || content.title;

  useDocumentMetadata({
    // The tab says it first. See this file's header on why the title carries the mark as well.
    title: `[Preview] ${pageTitle}`,
    description: '',
    noIndex: true,
  });

  return (
    <>
      <div
        // `role="status"` rather than `alert`: it is a standing statement about the page, not an
        // interruption, and a screen reader should announce it on arrival without stealing focus.
        role="status"
        data-testid="cms-preview-banner"
        className="sticky top-0 z-50 border-b border-amber-300 bg-amber-100 px-6 py-3 text-center text-sm text-amber-950"
      >
        <strong className="font-semibold">Preview — this page is not published.</strong>{' '}
        <span>
          You are looking at a draft. What is on the live site may be different. This link stops
          working on {formatExpiry(content.previewExpiresAt)}.
        </span>
      </div>
      <Sections sections={pageSections(content.data)} />
    </>
  );
}

/**
 * The expiry as a sentence, in the business timezone with the abbreviation visible.
 *
 * The zone is named for the reason `docs/standards/time-and-timezone.md` gives about display: the
 * reader may be anywhere, and "stops working on 23 Sep at 6:30" without a zone is a support
 * question rather than an answer.
 */
function formatExpiry(instant: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: 'America/New_York',
  }).format(new Date(instant));
}
